import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, AlertIcon, Box, Button, Checkbox, Flex, Input, InputGroup, InputLeftElement, Popover, PopoverBody, PopoverContent,
  PopoverTrigger, Select, Spinner, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useToast,
} from '@chakra-ui/react';
import { FiSearch } from 'react-icons/fi';
import useAuth from 'hooks/useAuth';
import AiPagination from 'components/monitoring/ai/AiPagination';
import {
  EDIT_ROLES, bulkCategory, undoBulkCategory, getFinanceOptions, getMovements, errorMessage, formatAmount, formatDate, formatMoney,
} from 'api/administrador/finance';
import { Card, EmptyState, PageHeader, Tag } from 'components/finance/ui';
import CategoryAutocomplete from 'components/finance/CategoryAutocomplete';
import CategoriesModal from 'components/finance/CategoriesModal';

const PAGE_SIZE = 20;

/**
 * Asignar categoría en bloque: busca en todo el histórico de un libro, selecciona la página o todos
 * los resultados y les asigna una categoría. Queda en Auditoría y se puede deshacer.
 */
export default function AsignarCategorias() {
  const toast = useToast();
  const { user } = useAuth();
  const canEdit = EDIT_ROLES.includes(user?.role?.type);
  const [params] = useSearchParams();
  const book = params.get('libro') === 'caja_menor' ? 'caja_menor' : 'bancos';

  const [options, setOptions] = useState(null);
  const [filters, setFilters] = useState({ account: '', q: '', type: 'ingreso', category: 'none' });
  const [appliedQ, setAppliedQ] = useState('');
  const [page, setPage] = useState(1);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [allSelected, setAllSelected] = useState(false);
  const [categoryId, setCategoryId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const [categoriesOpen, setCategoriesOpen] = useState(null);

  const loadOptions = useCallback(() => getFinanceOptions().then(setOptions).catch((e) => toast({ status: 'error', title: errorMessage(e) })), [toast]);
  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const accounts = useMemo(() => (options?.accounts || []).filter((a) => a.book === book), [options, book]);
  const filterParams = useMemo(() => ({ book, account: filters.account, q: appliedQ, type: filters.type, category: filters.category }), [book, filters.account, appliedQ, filters.type, filters.category]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setList(await getMovements({ all: 1, ...filterParams, page, pageSize: PAGE_SIZE }));
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e) });
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
    setSelected(new Set());
    setAllSelected(false);
  }, [filterParams]);

  const rows = list?.data || [];
  const total = list?.pagination?.total || 0;
  const pageIds = rows.map((r) => r.id);
  const pageChecked = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const count = allSelected ? total : selected.size;

  const togglePage = () => {
    const next = new Set(selected);
    if (pageChecked) pageIds.forEach((id) => next.delete(id));
    else pageIds.forEach((id) => next.add(id));
    setSelected(next);
    setAllSelected(false);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
    setAllSelected(false);
  };

  async function assign() {
    setSaving(true);
    try {
      const body = allSelected ? { filter: filterParams, categoryId } : { ids: [...selected], categoryId };
      const result = await bulkCategory(body);
      setDone(result);
      setSelected(new Set());
      setAllSelected(false);
      setCategoryId(null);
      load();
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e), duration: 6000, isClosable: true });
    } finally {
      setSaving(false);
    }
  }

  async function undo() {
    try {
      await undoBulkCategory(done.auditId);
      toast({ status: 'success', title: 'Se deshizo la asignación', duration: 3000 });
      setDone(null);
      load();
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e) });
    }
  }

  const type = filters.type === 'ingreso' || filters.type === 'egreso' ? filters.type : null;
  const backTo = book === 'caja_menor' ? '/plataforma/administrador/finanzas/flujo-caja' : '/plataforma/administrador/finanzas/bancos';

  return (
    <Box bg="gray.50" mx="-20px" mt="-20px" p={{ base: 4, md: 8 }} minH="calc(100vh - 80px)">
      <PageHeader
        breadcrumb={[{ label: 'Finanzas', to: '/plataforma/administrador/finanzas' }, { label: book === 'caja_menor' ? 'Flujo de caja' : 'Bancos', to: backTo }, { label: 'Asignar en bloque' }]}
        title="Asignar categoría en bloque"
        subtitle="Busca en todo el histórico, selecciona los resultados y asígnales una categoría de una vez."
        actions={<Tag bg="purple.50" color="purple.800">Periodo: todo el histórico</Tag>}
      />

      <Card padding={4} mb={4}>
        <Flex gap={2.5} wrap="wrap" align="center">
          <InputGroup flex="1 1 260px" size="sm">
            <InputLeftElement pointerEvents="none"><FiSearch color="#a0aec0" /></InputLeftElement>
            <Input
              aria-label="Buscar"
              placeholder="Ej.: WOMPI, NOMINA, CESANTIA"
              bg="white"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && setAppliedQ(filters.q)}
              onBlur={() => setAppliedQ(filters.q)}
            />
          </InputGroup>
          <Select size="sm" w="auto" bg="white" aria-label="Cuenta" value={filters.account} onChange={(e) => setFilters((f) => ({ ...f, account: e.target.value }))}>
            <option value="">Todas las cuentas</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
          <Select size="sm" w="auto" bg="white" aria-label="Tipo" value={filters.type} onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}>
            <option value="ingreso">Ingresos</option>
            <option value="egreso">Egresos</option>
          </Select>
          <Select size="sm" w="auto" bg="white" aria-label="Categoría" value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}>
            <option value="none">Sin categoría</option>
            <option value="">Todas</option>
            {(options?.categories || []).filter((c) => c.book === book).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Text fontSize="sm" color="gray.600" ml="auto">
            <b>{total.toLocaleString('es-CO')}</b> resultados · {formatMoney(filters.type === 'egreso' ? list?.totals?.out : list?.totals?.in)}
          </Text>
        </Flex>
      </Card>

      {done && (
        <Alert status="success" borderRadius="lg" mb={4} flexWrap="wrap" gap={2}>
          <AlertIcon />
          <Text flex={1} fontSize="sm">
            Se asignó <b>{done.category ? `«${done.category.name}»` : 'sin categoría'}</b> a {done.updated.toLocaleString('es-CO')} movimientos. Quedó en Auditoría.
          </Text>
          <Button size="sm" variant="link" colorScheme="purple" onClick={undo}>Deshacer</Button>
        </Alert>
      )}

      {canEdit && count > 0 && (
        <Flex bg="#574feb" color="white" borderRadius="lg" px={4} py={2.5} mb={4} gap={3} align="center" wrap="wrap">
          <Text fontWeight="700">{count.toLocaleString('es-CO')} seleccionados</Text>
          {!allSelected && pageChecked && total > rows.length && (
            <>
              <Text fontSize="sm" opacity={0.9}>Solo los de esta página.</Text>
              <Button size="sm" bg="whiteAlpha.300" color="white" _hover={{ bg: 'whiteAlpha.400' }} onClick={() => setAllSelected(true)}>
                Seleccionar los {total.toLocaleString('es-CO')} resultados
              </Button>
            </>
          )}
          <Box flex={1} />
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <Button size="sm" bg="white" color="gray.800">Asignar categoría</Button>
            </PopoverTrigger>
            <PopoverContent color="gray.800" w="340px">
              <PopoverBody display="flex" flexDirection="column" gap={3} p={3}>
                <Text fontSize="sm" fontWeight="500">Categoría {type ? `de ${type}` : ''}</Text>
                <CategoryAutocomplete id="bulk-category" categories={options?.categories} book={book} type={type} value={categoryId} onChange={setCategoryId} onCreate={(name, kind) => setCategoriesOpen({ name, kind })} />
                <Text fontSize="xs" color="gray.500">Reemplaza la categoría de los {count.toLocaleString('es-CO')} movimientos seleccionados. Si no eliges ninguna, quedan sin categoría.</Text>
                <Button bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} onClick={assign} isLoading={saving}>
                  Asignar a {count.toLocaleString('es-CO')} movimientos
                </Button>
              </PopoverBody>
            </PopoverContent>
          </Popover>
          <Button size="sm" variant="ghost" color="white" _hover={{ bg: 'whiteAlpha.200' }} onClick={() => { setSelected(new Set()); setAllSelected(false); }}>Quitar selección</Button>
        </Flex>
      )}

      <Card padding={0}>
        <TableContainer>
          <Table size="sm">
            <Thead>
              <Tr>
                {canEdit && <Th bg="gray.50" w="36px"><Checkbox aria-label="Seleccionar la página" isChecked={pageChecked || allSelected} onChange={togglePage} /></Th>}
                <Th bg="gray.50">Fecha</Th>
                <Th bg="gray.50">Cuenta</Th>
                <Th bg="gray.50">Concepto</Th>
                <Th bg="gray.50">Categoría</Th>
                <Th bg="gray.50" isNumeric>Monto</Th>
                <Th bg="gray.50">Origen</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((m) => (
                <Tr key={m.id} bg={allSelected || selected.has(m.id) ? 'purple.50' : undefined}>
                  {canEdit && <Td><Checkbox aria-label="Seleccionar movimiento" isChecked={allSelected || selected.has(m.id)} onChange={() => toggleOne(m.id)} /></Td>}
                  <Td color="gray.600" whiteSpace="nowrap">{formatDate(m.date)}</Td>
                  <Td>{m.accountName}</Td>
                  <Td whiteSpace="normal" fontWeight="500">{m.concept}</Td>
                  <Td>{m.category ? <Tag>{m.category.name}</Tag> : <Tag bg="gray.50" color="gray.500">Sin categoría</Tag>}</Td>
                  <Td isNumeric color={m.type === 'ingreso' ? 'green.700' : undefined} fontWeight={m.type === 'ingreso' ? '600' : undefined}>{formatAmount(m.amount)}</Td>
                  <Td fontSize="xs" color="gray.500">{m.source === 'importado' ? 'Importado del Excel' : 'Registrado en el admin'}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
        {loading && <Flex justify="center" py={3}><Spinner size="sm" /></Flex>}
        {list && rows.length === 0 && <EmptyState title="No hay movimientos con estos filtros" description="Prueba con otra palabra o quita el filtro de categoría." />}
        <AiPagination pagination={list?.pagination} noun="movimientos" onPageChange={setPage} />
      </Card>

      <CategoriesModal
        isOpen={categoriesOpen !== null}
        onClose={() => setCategoriesOpen(null)}
        book={book}
        categories={options?.categories}
        canEdit={canEdit}
        initialName={categoriesOpen?.name}
        initialKind={categoriesOpen?.kind}
        onChanged={loadOptions}
      />
    </Box>
  );
}
